import { c as _c } from "react/compiler-runtime";
import { Download, TriangleAlert } from 'lucide-react';
import { useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useExportBackupFile } from '@/hooks/backup/use-export-backup-file';
import { useLoadDemoData } from '@/hooks/demo/use-load-demo-data';
import { usePianoProfileState } from '@/hooks/store/use-piano-profile-store';
import { useTranslation } from '@/hooks/use-translation';
export interface LoadDemoDialogProps {
  isOpen: boolean;
  /** Keys carrying both a down and an up weight, for the warning. */
  measured: number;
  onOpenChange: (open: boolean) => void;
  total: number;
}

/**
 * What stands between someone's piano and the demonstration one.
 *
 * The application keeps a single profile, so loading the demo overwrites it.
 * The way out is offered before the door rather than after: export, then
 * load — and the export is the button that carries the weight.
 */
export const LoadDemoDialog = t0 => {
  const $ = _c(71);
  const {
    isOpen,
    measured,
    onOpenChange,
    total
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    brand,
    model
  } = usePianoProfileState();
  const {
    exportBackupFile
  } = useExportBackupFile();
  const {
    loadDemoData
  } = useLoadDemoData();
  const exportButton = useRef(null);
  let t1;
  if ($[0] !== loadDemoData || $[1] !== onOpenChange) {
    t1 = () => {
      loadDemoData();
      onOpenChange(false);
    };
    $[0] = loadDemoData;
    $[1] = onOpenChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const load = t1;
  let t2;
  if ($[3] !== exportBackupFile || $[4] !== load) {
    t2 = () => {
      exportBackupFile();
      load();
    };
    $[3] = exportBackupFile;
    $[4] = load;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const exportThenLoad = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => {
      event.preventDefault();
      exportButton.current?.focus();
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("Demonstration piano");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="ptl-label">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("Replace your profile with the demo?");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <DialogTitle>{t6}</DialogTitle>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <DialogHeader className="gap-2 pr-9">{t5}{t7}</DialogHeader>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("Piano Touch Lab keeps one profile at a time. The demonstration piano will replace yours: export it first and you will be able to import it again from File \u203A Import.");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <DialogDescription className="text-[15px] leading-[1.6]">{t9}</DialogDescription>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <TriangleAlert />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = brand && model ? " " : null;
  let t13;
  if ($[23] !== model) {
    t13 = model ? <em className="italic">{model}</em> : null;
    $[23] = model;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== brand || $[26] !== t12 || $[27] !== t13) {
    t14 = <span className="font-serif text-[17px] text-foreground">{brand}{t12}{t13}</span>;
    $[25] = brand;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== measured || $[30] !== t || $[31] !== total) {
    t15 = t("{{measured}}/{{total}} keys measured \xB7 design and options", {
      measured,
      total
    });
    $[29] = measured;
    $[30] = t;
    $[31] = total;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t15) {
    t16 = <span className="font-mono text-[12.5px]">{t15}</span>;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14 || $[36] !== t16) {
    t17 = <Alert variant="default">{t11}<AlertDescription className="gap-1">{t14}{t16}</AlertDescription></Alert>;
    $[35] = t14;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("Replace without exporting");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== load || $[41] !== t18) {
    t19 = <Button variant="link" className="text-destructive" onClick={load}>{t18}</Button>;
    $[40] = load;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== onOpenChange) {
    t20 = () => onOpenChange(false);
    $[43] = onOpenChange;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t) {
    t21 = t("Cancel");
    $[45] = t;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t20 || $[48] !== t21) {
    t22 = <Button variant="outline" onClick={t20}>{t21}</Button>;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Download className="size-4" />;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t) {
    t24 = t("Export, then load");
    $[51] = t;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== exportThenLoad || $[54] !== t24) {
    t25 = <Button ref={exportButton} onClick={exportThenLoad}>{t23}{t24}</Button>;
    $[53] = exportThenLoad;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t22 || $[57] !== t25) {
    t26 = <div className="flex flex-wrap gap-2.5">{t22}{t25}</div>;
    $[56] = t22;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t19 || $[60] !== t26) {
    t27 = <DialogFooter className="items-center sm:justify-between">{t19}{t26}</DialogFooter>;
    $[59] = t19;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t10 || $[63] !== t17 || $[64] !== t27 || $[65] !== t8) {
    t28 = <DialogContent onOpenAutoFocus={t3}>{t8}{t10}{t17}{t27}</DialogContent>;
    $[62] = t10;
    $[63] = t17;
    $[64] = t27;
    $[65] = t8;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== isOpen || $[68] !== onOpenChange || $[69] !== t28) {
    t29 = <Dialog open={isOpen} onOpenChange={onOpenChange}>{t28}</Dialog>;
    $[67] = isOpen;
    $[68] = onOpenChange;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  return t29;
};